import { axiosInstance } from '@/Api/axiosV2Config';
import type { ContractFilters, ContractListResponse, Contract, PaymentDetails, CreateContractDto, ContractResponse, ContractDraft, ContractRefund, ContractRefundRequestResult, RefundablePaymentInfo } from '@/features/contracts/types/contract.types';

export interface SavAccessEmailPreview {
  subject: string;
  htmlContent: string;
  recipient: { name: string; email: string | null };
  guarantor: { name: string; logoUrl: string | null };
  declarationUrl: string;
  blockingReason: string | null;
}

export interface ReactivateStripeSubscriptionResult {
  status: 'executed' | 'dry_run' | 'no_op';
  contractId: number;
  subscriptionUuid: string;
  oldSubscriptionId: string;
  oldSubStatus: string;
  newSubscriptionId: string | null;
  paidCount: number;
  iterations: number;
  monthlyPriceClient: number;
  unitAmountCents: number;
  feePercentage: number;
  customerId: string;
  paymentMethod: {
    id: string;
    type: 'sepa_debit';
    sepaLast4: string | null;
    bankCode: string | null;
    country: string | null;
  };
  missedCount: number;
  catchUpAmountCents: number;
  firstSubChargeAtSec: number | null;
  catchUpInvoiceId: string | null;
}

/** Récapitulatif du report du 1er prélèvement à la date de souscription. */
export interface FirstChargePostponePreview {
  contractId: number;
  contractLabel: string;
  subscriptionId: string;
  /** ISO : 1er prélèvement reporté (date de souscription). */
  firstChargeAt: string;
  /** ISO : dernière mensualité. */
  lastChargeAt: string;
  iterations: number;
  monthlyAmountCents: number;
  /** Prélèvement à rembourser ; null s'il n'y en a pas encore eu. */
  payment: {
    amountCents: number;
    status: 'succeeded' | 'processing';
    paymentDate: string;
    commissionCents: number;
  } | null;
  subscriptionAlreadyPostponed: boolean;
  refundAlreadyRequested: boolean;
  /** Destinataires des e-mails au client : le client, avec copie au vendeur. */
  emails?: { beneficiary: string | null; vendorCopy: string | null };
  /** Simulation : l'e-mail envoyé au client dès la confirmation, tel qu'il partira. */
  emailPreview?: { subject: string; htmlContent: string } | null;
}

export type FirstChargePostponeResponse =
  | { eligible: false; reason: string }
  | {
      eligible: true;
      executed: boolean;
      preview: FirstChargePostponePreview;
      refund: ContractRefundRequestResult | null;
    };

export interface RegenerateInvoicePreview {
  contractId: number;
  contractLabel: string;
  companyCode: string;
  invoiceLabel: string;
  iteration: number;
  monthlyTtc: number;
  totalTtc: number;
  firstChargeDate: string;
  lastChargeDate: string;
  stripeInvoiceId: string;
}

export type RegenerateMonthlyInvoiceResponse =
  | { eligible: false; reason: string }
  | { eligible: true; regenerated: false; preview: RegenerateInvoicePreview }
  | {
      eligible: true;
      regenerated: true;
      documentId: number;
      label: string;
      stripeInvoiceId: string;
      monthlyTtc: number;
      iteration: number;
    };

/**
 * API client partagé pour la gestion des contrats
 * Contient les méthodes de lecture utilisées par admin et outil-pro
 */
export const contractApi = {
  /**
   * Lister les contrats avec filtres et pagination
   */
  getContracts: async (filters?: ContractFilters): Promise<ContractListResponse> => {
    const { data } = await axiosInstance.get<ContractListResponse>('/pro-v2/contracts', {
      params: filters,
    });
    return data;
  },

  /**
   * Récupérer un contrat par ID
   */
  getContract: async (id: number): Promise<Contract> => {
    const { data } = await axiosInstance.get<Contract>(`/pro-v2/contracts/${id}`);
    return data;
  },

  /**
   * Récupérer les détails de paiement d'un contrat
   */
  getPaymentDetails: async (id: number): Promise<PaymentDetails> => {
    const { data } = await axiosInstance.get<PaymentDetails>(`/pro-v2/contracts/${id}/payment-details`);
    return data;
  },

  /**
   * Télécharger la facture/échéancier d'un contrat
   */
  downloadInvoice: async (id: number): Promise<Blob> => {
    const { data } = await axiosInstance.get(`/pro-v2/contracts/${id}/invoice`, {
      responseType: 'blob',
    });
    return data;
  },

  /**
   * Régénérer la facture échéancier d'un contrat (admin INTERNAL).
   * dryRun=true → retourne le récap sans rien écraser.
   * dryRun=false → écrase le PDF SFTP en place et conserve le numéro de facture.
   */
  regenerateMonthlyInvoice: async (
    contractId: number,
    dryRun: boolean,
  ): Promise<RegenerateMonthlyInvoiceResponse> => {
    const { data } = await axiosInstance.post<RegenerateMonthlyInvoiceResponse>(
      `/pro-v2/contracts/${contractId}/admin/regenerate-monthly-invoice`,
      { dryRun },
    );
    return data;
  },

  /**
   * Télécharger le mandat de prélèvement SEPA client d'un contrat mensualisé
   */
  downloadSepaMandate: async (id: number): Promise<Blob> => {
    const { data } = await axiosInstance.get(`/pro-v2/contracts/${id}/sepa-mandate`, {
      responseType: 'blob',
    });
    return data;
  },

  /**
   * Récupérer le document PDF d'un contrat (blob pour visualisation)
   */
  getDocumentBlob: async (id: number): Promise<Blob> => {
    const { data } = await axiosInstance.get(`/pro-v2/contracts/${id}/document`, {
      responseType: 'blob',
    });
    return data;
  },

  /**
   * Télécharger le document PDF d'un contrat
   */
  downloadDocument: async (id: number): Promise<Blob> => {
    const { data } = await axiosInstance.get(`/pro-v2/contracts/${id}/document`, {
      responseType: 'blob',
      params: { download: 'true' },
    });
    return data;
  },

  /**
   * Télécharger un document supplémentaire du contrat
   */
  downloadContractDocument: async (documentId: number, fileName: string): Promise<void> => {
    const { data } = await axiosInstance.get(`/documents/${documentId}/download`, {
      responseType: 'blob',
      params: { downloadType: 'download' },
    });
    const url = URL.createObjectURL(data);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },

  /**
   * Créer un nouveau contrat
   */
  createContract: async (contract: CreateContractDto): Promise<ContractResponse> => {
    const { data } = await axiosInstance.post<ContractResponse>(
      '/pro-v2/contracts',
      contract,
    );
    return data;
  },

  /**
   * Générer un PDF de preview pour le wizard de création de contrat
   */
  generatePreviewPdf: async (previewData: {
    warrantyUuid: string;
    vehicle?: {
      brand?: string;
      model?: string;
      finishes?: string;
      registration?: string;
      serialNumber?: string;
      firstCirculation?: string;
      vehicleSearchType?: number;
    };
    beneficiary?: {
      firstName?: string;
      lastName?: string;
      email?: string;
      phoneNumber?: string;
      address?: string;
      address2?: string;
      cp?: string;
      city?: string;
      society?: string;
    };
    subscriptionDate?: string;
    kmStart?: number;
    vehicleSellingPrice?: number;
    sellingPrice?: number;
    paymentType?: number;
    monthlyPriceWithFees?: number;
    includedOption?: boolean;
    includedOptionLabel?: string;
    isIntermediary?: boolean;
  }): Promise<Blob> => {
    const { data } = await axiosInstance.post(
      '/pro-v2/contracts/preview-pdf',
      previewData,
      { responseType: 'blob' },
    );
    return data;
  },

  /**
   * Modifier les informations bénéficiaire et véhicule d'un contrat (INTERNAL only)
   *
   * `signatureResend` n'est renvoyé que si le PDF est regénéré : `resent` (nouvelle
   * demande de signature envoyée), `skipped` (rien à renvoyer) ou `failed` (à relancer
   * depuis la fiche contrat).
   */
  editContractInfo: async (id: number, data: {
    beneficiary?: { firstName?: string; lastName?: string; email?: string; phoneNumber?: string };
    vehicle?: { registration?: string; kmStart?: string };
    regeneratePdf?: boolean;
  }): Promise<{ message: string; signatureResend?: 'resent' | 'skipped' | 'failed' }> => {
    const response = await axiosInstance.patch(`/pro-v2/contracts/${id}/edit-info`, data);
    return response.data;
  },

  /**
   * Relancer la signature électronique d'un contrat
   */
  retrySignature: async (
    id: number,
    overrides?: { email?: string; phone?: string; firstname?: string; lastname?: string },
  ): Promise<{ message: string; signatureLink?: string; signatureLinkExpiration?: string }> => {
    const { data } = await axiosInstance.post(`/pro-v2/contracts/${id}/signature/retry`, overrides || {});
    return data;
  },

  /**
   * Annuler un contrat (admin) — annule signature YouSign et paiement Stripe
   */
  cancelContract: async (
    id: number,
    options: { skipAvoir?: boolean; refund?: boolean; refundCommission?: boolean } = {},
  ): Promise<{ refund: ContractRefundRequestResult | null }> => {
    const { data } = await axiosInstance.post<{ refund: ContractRefundRequestResult | null }>(
      `/pro-v2/contracts/${id}/cancel`,
      options,
    );
    return { refund: data?.refund ?? null };
  },

  /**
   * Rembourser le dernier paiement d'un contrat déjà annulé (admin)
   */
  refundCancelledContract: async (
    id: number,
    options: { refundCommission?: boolean } = {},
  ): Promise<{ refund: ContractRefundRequestResult }> => {
    const { data } = await axiosInstance.post<{ refund: ContractRefundRequestResult }>(
      `/pro-v2/contracts/${id}/refund`,
      options,
    );
    return { refund: data.refund };
  },

  /**
   * Dernier paiement remboursable (annulation, ou remboursement d'un contrat annulé)
   */
  getRefundablePayment: async (id: number): Promise<RefundablePaymentInfo> => {
    const { data } = await axiosInstance.get<RefundablePaymentInfo>(`/pro-v2/contracts/${id}/refundable-payment`);
    return data;
  },

  /**
   * Reporter le 1er prélèvement à la date de souscription (admin INTERNAL, permission
   * dédiée) : abonnement reporté, prélèvement déjà fait remboursé, commission rendue.
   * `dryRun` : récapitulatif sans rien modifier.
   */
  postponeFirstCharge: async (id: number, dryRun: boolean): Promise<FirstChargePostponeResponse> => {
    const { data } = await axiosInstance.post<FirstChargePostponeResponse>(
      `/pro-v2/contracts/${id}/postpone-first-charge`,
      { dryRun },
    );
    return data;
  },

  /**
   * Demandes de remboursement client d'un contrat
   */
  getContractRefunds: async (id: number): Promise<ContractRefund[]> => {
    const { data } = await axiosInstance.get<ContractRefund[]>(`/pro-v2/contracts/${id}/refunds`);
    return data;
  },

  /**
   * Demander l'annulation d'un contrat (prescripteur/vendeur)
   */
  requestCancellation: async (id: number, reason: string, details?: string): Promise<void> => {
    await axiosInstance.post(`/pro-v2/contracts/${id}/request-cancellation`, { reason, details });
  },

  /**
   * Aperçu de l'email « infos de connexion pour déclarer une panne » (co-brandé garant + Affinicar)
   */
  getSavAccessEmailPreview: async (id: number): Promise<SavAccessEmailPreview> => {
    const { data } = await axiosInstance.get<SavAccessEmailPreview>(`/pro-v2/contracts/${id}/sav-access-email/preview`);
    return data;
  },

  /**
   * Envoyer au bénéficiaire ses infos de connexion pour déclarer une panne
   */
  sendSavAccessEmail: async (id: number): Promise<{ success: boolean; emailId: number | null }> => {
    const { data } = await axiosInstance.post(`/pro-v2/contracts/${id}/sav-access-email`);
    return data;
  },

  /**
   * Offrir un contrat — crée un avoir (IS_AVOIR = false)
   */
  offerContract: async (id: number): Promise<void> => {
    await axiosInstance.post(`/pro-v2/contracts/${id}/offer`);
  },

  /**
   * Vérifier si un contrat a été offert
   */
  isContractOffered: async (id: number): Promise<boolean> => {
    const { data } = await axiosInstance.get<{ isOffered: boolean }>(`/pro-v2/contracts/${id}/is-offered`);
    return data.isOffered;
  },

  /**
   * Régénérer le lien de paiement Stripe d'un contrat
   */
  regeneratePaymentLink: async (contractId: number): Promise<{ success: boolean; paymentLink?: string; message: string }> => {
    const { data } = await axiosInstance.post<{ success: boolean; paymentLink?: string; message: string }>(
      '/stripe/regenerate-payment-link',
      { contractId },
    );
    return data;
  },

  /**
   * Générer un lien pour mettre à jour le moyen de paiement (SEPA) d'un contrat mensualisé
   */
  updatePaymentMethod: async (contractId: number, sendNotification?: boolean, email?: string, phone?: string): Promise<{ success: boolean; paymentLink?: string; message: string }> => {
    const { data } = await axiosInstance.post<{ success: boolean; paymentLink?: string; message: string }>(
      '/stripe/update-payment-method',
      { contractId, sendNotification, email, phone },
    );
    return data;
  },

  /**
   * Renvoyer la notification de paiement (email + SMS) au bénéficiaire
   */
  resendPaymentNotification: async (contractId: number, email?: string, phone?: string): Promise<{ success: boolean; message: string }> => {
    const { data } = await axiosInstance.post<{ success: boolean; message: string }>(
      '/stripe/resend-payment-method-confirmation',
      { contractId, email, phone },
    );
    return data;
  },

  /**
   * Uploader le contrat signé (signature manuscrite)
   */
  uploadSignedDocument: async (id: number, file: File): Promise<{ documentId: number }> => {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await axiosInstance.post<{ documentId: number }>(
      `/pro-v2/contracts/${id}/upload-signed-document`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return data;
  },

  /**
   * Export CSV des contrats (admin only)
   */
  exportContractsCsv: async (filters?: Pick<ContractFilters, 'search' | 'status' | 'creationDateFrom' | 'creationDateTo' | 'companyId'>): Promise<Blob> => {
    const { data } = await axiosInstance.get('/admin/contracts/export', {
      params: {
        search: filters?.search,
        status: filters?.status,
        dateFrom: filters?.creationDateFrom,
        dateTo: filters?.creationDateTo,
        companyId: filters?.companyId,
      },
      responseType: 'blob',
      timeout: 600000, // 10 min pour les gros exports
    });
    return data;
  },

  /**
   * Export CSV des SAV (admin only)
   */
  exportSavsCsv: async (): Promise<Blob> => {
    const { data } = await axiosInstance.get('/admin/savs/export', {
      responseType: 'blob',
      timeout: 600000, // 10 min pour les gros exports
    });
    return data;
  },

  /**
   * Upload du rapport de vérification véhicule
   */
  uploadVehicleCheckReport: async (file: File): Promise<{ fileUrl: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await axiosInstance.post<{ fileUrl: string }>(
      '/pro-v2/contracts/upload-vehicle-check',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return data;
  },

  /**
   * Réactive un contrat annulé en recréant une Subscription Stripe sur le
   * même mandat SEPA (admin super-admin only). En mode dryRun, retourne la
   * photo de ce qui serait créé sans muter.
   */
  reactivateStripeSubscription: async (
    subscriptionUuid: string,
    body: { dryRun?: boolean; remainingIterations?: number },
  ): Promise<ReactivateStripeSubscriptionResult> => {
    const { data } = await axiosInstance.post<ReactivateStripeSubscriptionResult>(
      `/contracts/${subscriptionUuid}/reactivate-stripe-subscription`,
      body,
    );
    return data;
  },

  // ==================== BROUILLONS ====================

  getDrafts: async (): Promise<ContractDraft[]> => {
    const { data } = await axiosInstance.get<ContractDraft[]>('/pro-v2/contracts/drafts');
    return data;
  },

  getDraft: async (uuid: string): Promise<ContractDraft> => {
    const { data } = await axiosInstance.get<ContractDraft>(`/pro-v2/contracts/drafts/${uuid}`);
    return data;
  },

  saveDraft: async (payload: { formData: Record<string, any>; currentStep: number; label?: string }): Promise<ContractDraft> => {
    const { data } = await axiosInstance.post<ContractDraft>('/pro-v2/contracts/drafts', payload);
    return data;
  },

  updateDraft: async (uuid: string, payload: { formData?: Record<string, any>; currentStep?: number; label?: string }): Promise<ContractDraft> => {
    const { data } = await axiosInstance.patch<ContractDraft>(`/pro-v2/contracts/drafts/${uuid}`, payload);
    return data;
  },

  deleteDraft: async (uuid: string): Promise<void> => {
    await axiosInstance.delete(`/pro-v2/contracts/drafts/${uuid}`);
  },
};
